import React, { useState } from 'react';
import { Input, Icon, Button, Segment, Label, Menu } from 'semantic-ui-react';
import GlassmorphismTheme from '../../../styles/glassmorphism';
import { Conversation, ConversationFilters, ConversationStatus } from '../types/conversation.types';

interface ConversationListProps {
  conversations: Conversation[];
  selectedId?: string;
  onSelect: (conversation: Conversation) => void;
  onFilterChange: (filters: Partial<ConversationFilters>) => void;
  filters: ConversationFilters;
}

const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  selectedId,
  onSelect,
  onFilterChange,
  filters,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<ConversationStatus | 'TODOS'>('TODOS');

  // ===== FILTER CONVERSATIONS =====
  const filtered = conversations.filter(conv => {
    const matchesSearch = conv.nomeCliente.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'TODOS' || conv.statusFluxo === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // ===== STATUS COLOR =====
  const getStatusColor = (status: ConversationStatus) => {
    const colors: Record<ConversationStatus, string> = {
      'PENDENTE': '#f39c12',
      'TEMPLATE_ENVIADO': '#3498db',
      'AGUARDANDO_RESPOSTA': '#e74c3c',
      'EM_FLUXO_AUTOMATIZADO': '#9b59b6',
      'OFERTA_ACEITA': '#27ae60',
      'OFERTA_RECUSADA': '#95a5a6',
      'AGUARDANDO_ATENDENTE': '#e67e22',
      'FINALIZADO': '#95a5a6',
    };
    return colors[status];
  };

  // ===== STATUS OPTIONS =====
  const statusOptions: Array<{ key: ConversationStatus | 'TODOS'; text: string; value: ConversationStatus | 'TODOS' }> = [
    { key: 'TODOS', text: 'Todos', value: 'TODOS' },
    { key: 'AGUARDANDO_RESPOSTA', text: 'Aguardando', value: 'AGUARDANDO_RESPOSTA' },
    { key: 'EM_FLUXO_AUTOMATIZADO', text: 'Em Fluxo', value: 'EM_FLUXO_AUTOMATIZADO' },
    { key: 'OFERTA_ACEITA', text: 'Aceitas', value: 'OFERTA_ACEITA' },
    { key: 'OFERTA_RECUSADA', text: 'Recusadas', value: 'OFERTA_RECUSADA' },
  ];

  return (
    <div style={{
      ...GlassmorphismTheme.components.glassCard,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    }}>
      {/* HEADER */}
      <h3 style={{ margin: '0 0 15px 0', color: '#2c3e50' }}>
        <Icon name="comments" /> Conversas
      </h3>

      {/* SEARCH */}
      <Input
        icon="search"
        placeholder="Buscar cliente..."
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          onFilterChange({ cliente: e.target.value });
        }}
        style={{
          marginBottom: GlassmorphismTheme.spacing.md,
          ...GlassmorphismTheme.components.inputGlass,
        }}
        fluid
      />

      {/* STATUS FILTER */}
      <Menu compact fluid vertical style={{ marginBottom: GlassmorphismTheme.spacing.md }}>
        {statusOptions.map(option => (
          <Menu.Item
            key={option.key}
            active={statusFilter === option.value}
            onClick={() => {
              setStatusFilter(option.value);
              onFilterChange({ status: option.value === 'TODOS' ? undefined : option.value });
            }}
            style={{
              background: statusFilter === option.value ? 'rgba(102, 126, 234, 0.2)' : 'transparent',
              color: statusFilter === option.value ? '#667eea' : '#2c3e50',
            }}
          >
            {option.text}
            {option.value !== 'TODOS' && (
              <Label 
                circular 
                style={{
                  background: getStatusColor(option.value),
                  color: 'white',
                }}
              >
                {conversations.filter(c => c.statusFluxo === option.value).length}
              </Label>
            )}
          </Menu.Item>
        ))}
      </Menu>

      {/* CONVERSATIONS LIST */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {filtered.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#7f8c8d', padding: '20px' }}>
            Nenhuma conversa encontrada
          </p>
        ) : (
          filtered.map(conversation => (
            <Segment
              key={conversation.id}
              onClick={() => onSelect(conversation)}
              style={{
                cursor: 'pointer',
                background: selectedId === conversation.id 
                  ? 'rgba(102, 126, 234, 0.15)' 
                  : 'rgba(255, 255, 255, 0.5)',
                border: selectedId === conversation.id
                  ? `2px solid #667eea`
                  : GlassmorphismTheme.borders.glassLight,
                backdropFilter: 'blur(10px)',
                marginBottom: GlassmorphismTheme.spacing.sm,
                padding: GlassmorphismTheme.spacing.md,
                transition: GlassmorphismTheme.transitions.normal,
              }}
              onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
                (e.currentTarget as HTMLElement).style.background = 'rgba(102, 126, 234, 0.2)';
              }}
              onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
                (e.currentTarget as HTMLElement).style.background = selectedId === conversation.id 
                  ? 'rgba(102, 126, 234, 0.15)' 
                  : 'rgba(255, 255, 255, 0.5)';
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <strong style={{ color: '#2c3e50' }}>{conversation.nomeCliente}</strong>
                  <p style={{
                    margin: '5px 0 0 0',
                    fontSize: '0.85rem',
                    color: '#7f8c8d',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                    {conversation.ultimaMensagem || 'Sem mensagens'}
                  </p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '5px' }}>
                  {conversation.naoLidas > 0 && (
                    <Label 
                      circular 
                      style={{
                        background: '#e74c3c',
                        color: 'white',
                        minWidth: '24px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {conversation.naoLidas}
                    </Label>
                  )}
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: getStatusColor(conversation.statusFluxo),
                  }} />
                </div>
              </div>
            </Segment>
          ))
        )}
      </div>
    </div>
  );
};

export default ConversationList;
